import React, { useEffect, useMemo, useState } from 'react';

import useApiControl from '../../../../common/useApiControl';
import MemberFinishContentPC from './MemberFinishContentPC';
import MemberFinishContentSP from './MemberFinishContentSP';

const RESPONSIVE_MODE = {
  MOBILE: 'mobile',
  DESKTOP: 'desktop',
};

const parseAnswers = (rawAnswers) => {
  if (Array.isArray(rawAnswers)) {
    return rawAnswers;
  }

  if (typeof rawAnswers === 'string') {
    const trimmed = rawAnswers.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error parsing answers JSON', error);
      console.log(rawAnswers);
      return [];
    }
  }

  return [];
};

const normalizeExpectedPayoutPayload = (payload, choices) => {
  const nextPayouts = choices.map((_, selection) => ({
    selection,
    bet: 0,
    total_point: 0,
    rate: null,
    refund_point: 0,
    options: [],
  }));

  if (!Array.isArray(payload)) {
    return nextPayouts;
  }

  payload.forEach((item) => {
    if (!item || typeof item !== 'object') {
      return;
    }

    const selectionIndex = Number(item.selection);
    if (!Number.isInteger(selectionIndex) || selectionIndex < 0 || selectionIndex >= nextPayouts.length) {
      return;
    }

    const bet = Number(item.bet ?? item.total_point ?? item.totalPoint ?? item.point ?? 0);
    nextPayouts[selectionIndex] = {
      selection: selectionIndex,
      bet,
      total_point: Number(item.total_point ?? item.totalPoint ?? bet),
      rate: item.rate ?? null,
      refund_point: Number(item.refund_point ?? item.refundPoint ?? 0),
      options: Array.isArray(item.options) || (item.options && typeof item.options === 'object') ? item.options : [],
    };
  });

  return nextPayouts;
};

const MemberFinishContent = ({
  event,
  highlightedBetSelections = [],
  message,
  responsiveMode,
  round,
  summaryData,
}) => {
  const [expectedPayouts, setExpectedPayouts] = useState([]);
  const choices = useMemo(() => parseAnswers(round?.answers), [round?.answers]);
  const roundNumber = round?.number || event?.round;
  const params = useMemo(
    () => ({
      event_id: event?.id,
      number: roundNumber,
    }),
    [event?.id, roundNumber],
  );

  const { data: expectedPayoutData, loading: loadingExpectedPayout, executeRequest } = useApiControl({
    action: '/get_my_expected_payout',
    params,
    method: 'POST',
  });

  useEffect(() => {
    if (!event?.id || !roundNumber) {
      return;
    }

    executeRequest();
  }, [event?.id, roundNumber]);

  useEffect(() => {
    if (Array.isArray(expectedPayoutData?.payouts)) {
      setExpectedPayouts(normalizeExpectedPayoutPayload(expectedPayoutData.payouts, choices));
      return;
    }

    setExpectedPayouts(normalizeExpectedPayoutPayload([], choices));
  }, [choices, expectedPayoutData]);

  const contentProps = {
    choices,
    expectedPayouts,
    highlightedBetSelections,
    loadingMyBet: loadingExpectedPayout,
    message,
    responsiveMode,
    summaryData,
  };

  if (responsiveMode === RESPONSIVE_MODE.MOBILE) {
    return <MemberFinishContentSP {...contentProps} />;
  }

  return <MemberFinishContentPC {...contentProps} />;
};

export default MemberFinishContent;
